/**
 * population-segment-api.service.ts — V2.1
 *
 * Cliente API para endpoints de población por segmento.
 * Soporta ?method=census_network_length (default) y ?method=building_refined.
 */

import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

const getAuthToken = (): string | null => {
  try {
    const authStorage = localStorage.getItem('auth-storage');
    if (!authStorage) return null;
    const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
    return parsed.state?.token ?? null;
  } catch {
    return null;
  }
};

const getHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ─── Types ────────────────────────────────────────────────────────────────────

export type PopulationMethod = 'census_network_length' | 'building_refined';

export type PopulationSegmentRunStatus =
  | 'no_runs'
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed';

export interface PopulationSegmentStatus {
  runId:                    string | null;
  projectId:                string;
  status:                   PopulationSegmentRunStatus;
  method:                   string | null;
  sourceCensusYear:         number | null;
  totalPopulationInput:     number | null;
  totalPopulationAssigned:  number | null;
  totalPopulationUnassigned: number | null;
  totalHouseholdsInput:     number | null;
  totalHouseholdsAssigned:  number | null;
  edgeCount:                number | null;
  radioCount:               number | null;
  unassignedRadioCount:     number | null;
  excludedShortEdgesCount:  number | null;
  assignmentPct:            number | null;
  createdAt:                string | null;
  completedAt:              string | null;
  errorMessage:             string | null;
}

export interface PopulationSegmentFeatureProperties {
  edge_id:                  string;
  population_estimated:     number | null;
  households_estimated:     number | null;
  nbi_households_estimated: number | null;
  source_radios_count:      number | null;
  /** Present when method=building_refined */
  source_buildings_count:   number | null;
  network_length_m:         number | null;
  length_meters:            number | null;
  road_class:               string | null;
  method:                   string;
}

// ─── Detail ───────────────────────────────────────────────────────────────────

export interface PopulationSegmentContribution {
  radioCode:                  string;
  segmentLengthInsideRadioM:  number;
  radioNetworkLengthTotalM:   number;
  populationContribution:     number;
  householdsContribution:     number;
  nbiHouseholdsContribution:  number;
  contributionSharePct:       number;
}

export interface BuildingContribution {
  buildingCentroidId:        string;
  displayCode:               string | null;
  radioCode:                 string;
  residentialWeightM2:       number | null;
  weightSource:              'built_area' | 'footprint_fallback' | null;
  populationContribution:    number;
  householdsContribution:    number;
  nbiHouseholdsContribution: number;
  eligibilityReason:         string | null;
  associationConfidence:     number | null;
}

export interface PopulationSegmentDetail {
  runId:   string;
  method:  PopulationMethod;
  edgeId:  string;

  populationEstimated:    number | null;
  householdsEstimated:    number | null;
  nbiHouseholdsEstimated: number | null;

  networkLengthM:         number | null;
  populationPerKm:        number | null;
  householdsPerKm:        number | null;
  nbiHouseholdsPerKm:     number | null;

  sourceRadiosCount:      number;
  sourceBuildingsCount?:  number;

  /** Contributions por radio (census_network_length) */
  contributions?:          PopulationSegmentContribution[];

  /** Contributions por edificio — top 25 (building_refined) */
  buildingContributions?:  BuildingContribution[];

  runMetadata: {
    createdAt:                string;
    completedAt:              string | null;
    totalPopulationInput:     number | null;
    totalPopulationAssigned:  number | null;
    totalPopulationUnassigned: number | null;
  };

  reason?: 'no_population_assigned';
}

export interface PopulationSegmentGeoJSON {
  type:     'FeatureCollection';
  features: Array<{
    type:       'Feature';
    geometry:   object;
    properties: PopulationSegmentFeatureProperties;
  }>;
  meta: {
    runId:                  string;
    projectId:              string;
    method:                 string;
    totalPopulationInput:   number | null;
    totalPopulationAssigned: number | null;
    assignmentPct:          number | null;
    edgeCount:              number | null;
    completedAt:            string;
    featureCount:           number;
  };
}

// ─── API ──────────────────────────────────────────────────────────────────────

export const populationSegmentApi = {
  async getStatus(
    projectId: string,
    method: PopulationMethod = 'census_network_length',
  ): Promise<PopulationSegmentStatus> {
    const { data } = await axios.get<PopulationSegmentStatus>(
      `${API_URL}/projects/${projectId}/population-segments/status`,
      { headers: getHeaders(), params: { method } },
    );
    return data;
  },

  async getGeoJson(
    projectId: string,
    method: PopulationMethod = 'census_network_length',
  ): Promise<PopulationSegmentGeoJSON> {
    const { data } = await axios.get<PopulationSegmentGeoJSON>(
      `${API_URL}/projects/${projectId}/population-segments/geojson`,
      { headers: getHeaders(), params: { method } },
    );
    return data;
  },

  async runBase(
    projectId: string,
    dryRun = false,
  ): Promise<{ runId: string; status: string; message: string }> {
    const url = `${API_URL}/projects/${projectId}/population-segments/run-base${dryRun ? '?dryRun=1' : ''}`;
    const { data } = await axios.post<{ runId: string; status: string; message: string }>(
      url,
      undefined,
      { headers: getHeaders() },
    );
    return data;
  },

  async runBuildingRefined(
    projectId: string,
    dryRun = false,
  ): Promise<{ runId: string; status: string; message: string }> {
    const url = `${API_URL}/projects/${projectId}/population-segments/run-building-refined${dryRun ? '?dryRun=1' : ''}`;
    const { data } = await axios.post<{ runId: string; status: string; message: string }>(
      url,
      undefined,
      { headers: getHeaders() },
    );
    return data;
  },

  async getDetail(
    projectId: string,
    edgeId: string,
    method: PopulationMethod = 'census_network_length',
  ): Promise<PopulationSegmentDetail> {
    const { data } = await axios.get<PopulationSegmentDetail>(
      `${API_URL}/projects/${projectId}/population-segments/${edgeId}/detail`,
      { headers: getHeaders(), params: { method } },
    );
    return data;
  },
};
